import { z } from 'zod/v4'
import { PaginatedResponse } from './pagination'
import { SiteEnv } from './site'
import { BulkUpdateStack, StackStatus } from './stack'

/**
 * The deployment center is a **work queue**: it lists the stacks that need something done to them, and
 * nothing else. There is deliberately no "live and up to date" tab — a stack that is deployed and in sync
 * needs no action, and the media pages are where it is read.
 *
 * The three tabs are named after the stack state they hold, not after the action they offer, so that the
 * tab id, the derived state and `StackStatus`'s own vocabulary stay one word rather than three that have
 * to be mapped onto each other.
 */
export const DEPLOYMENT_CENTER_TABS = ['to-configure', 'out-of-sync', 'to-deploy'] as const
export const DeploymentCenterTabSchema = z.enum(DEPLOYMENT_CENTER_TABS)
export type DeploymentCenterTab = (typeof DEPLOYMENT_CENTER_TABS)[number]

export const isDeploymentCenterTab = (value: unknown): value is DeploymentCenterTab => DEPLOYMENT_CENTER_TABS.includes(value as DeploymentCenterTab)

/**
 * The only stack types the page can show. `SCOPE_LEVEL_STACK_TYPES` (`pbstck-bidder`, `spark-for-pub`)
 * carry no `siteId`, so they have no media to sit under and no deployment of their own to trigger.
 *
 * Restricting the query to these two is strictly stronger than the old page's
 * `name: { $ne: PBSTCK_BIDDER_STACK_NAME }`, which let `spark-for-pub` stacks through.
 */
export const DEPLOYMENT_CENTER_STACK_TYPES = ['pbs', 'adm'] as const
export const DeploymentCenterStackTypeSchema = z.enum(DEPLOYMENT_CENTER_STACK_TYPES)
export type DeploymentCenterStackType = (typeof DEPLOYMENT_CENTER_STACK_TYPES)[number]

export const isDeploymentCenterStackType = (value: unknown): value is DeploymentCenterStackType => DEPLOYMENT_CENTER_STACK_TYPES.includes(value as DeploymentCenterStackType)

/** One badge per tab. Counts stack groups on the two stack tabs, medias on `to-configure`. */
export type DeploymentCenterCounts = Record<DeploymentCenterTab, number>

/**
 * The state of one stack **group**, which is what a row is.
 *
 * `up-to-date` is derived but never listed: it is the state that means "no work to do", and dropping it
 * here rather than at the query edge is what lets `stackGroupStateOf` stay a total function.
 */
export const STACK_GROUP_STATES = ['out-of-sync', 'to-deploy', 'up-to-date'] as const
export type StackGroupState = (typeof STACK_GROUP_STATES)[number]

/** The stack states a deployment center ever reads. Archived versions are history, not work. */
export const DEPLOYABLE_STACK_STATUSES = ['to-deploy', 'live'] as const satisfies readonly StackStatus[]
export type DeployableStackStatus = (typeof DEPLOYABLE_STACK_STATUSES)[number]

/**
 * One version inside a stack group, carrying exactly what the bulk actions need and nothing heavier.
 *
 * `updatedAt` is not decoration: `bulkDeployStacks` refuses a payload whose `updatedAt` does not match
 * the stored one ("Outdated stack snapshot"), so it is this row's optimistic-concurrency token and must
 * survive the round trip. `snapshot`/`output`/`configuration` are deliberately absent — a page listing
 * a hundred groups would otherwise ship every ad unit id of every One stack to render a table.
 */
export type DeploymentCenterStackVersion = {
  id: string
  version: number
  status: DeployableStackStatus
  ratio: number
  isOutOfSync: boolean
  priority?: number | null
  updatedAt: string
}

/** A row on the Out of sync / Ready to deploy tabs: one stack group, with the media it deploys on. */
export type DeploymentCenterStackRow = {
  stackGroupId: string
  stackName: string
  type: DeploymentCenterStackType
  siteId: string
  mediaName: string
  mediaEnv: SiteEnv
  /** Exclusive — `stackGroupStateOf`. `up-to-date` groups never reach the client. */
  state: Exclude<StackGroupState, 'up-to-date'>
  latestVersion: number
  /**
   * Whether the group is mid-experiment — more than one version serving traffic.
   *
   * Never a filter: an A/B group is listed and acted on like any other. It is what the deploy's
   * confirmation reads, because deploying one replaces only its newest live version and takes over that
   * version's share, leaving the other where it is.
   */
  isAbDeployed: boolean
  /** Every to-deploy/live version of the group, newest first. Feeds the expanded detail and the actions. */
  versions: DeploymentCenterStackVersion[]
}

/**
 * A row on the To configure tab — the one tab whose subject is a media rather than a stack, because a
 * media with no stack has no stack to point at. The old page faked one with `uniqueId()` to fit its
 * site-shaped table; this type is that hack's replacement.
 */
export type DeploymentCenterMediaRow = {
  siteId: string
  mediaName: string
  mediaEnv: SiteEnv
  /** Products the media is activated on but carries no live/to-deploy stack for. Never empty. */
  missingProducts: DeploymentCenterStackType[]
}

/**
 * Counts ship with the rows on every response, rather than from a separate call.
 *
 * This is what makes a badge and its table incapable of disagreeing: both are read out of one
 * aggregation, under one filter, with the Node gate already applied. Three parallel count queries would
 * reintroduce exactly the divergence the gate exists to prevent.
 */
export type DeploymentCenterResponse =
  | { tab: 'to-configure'; counts: DeploymentCenterCounts; medias: PaginatedResponse<DeploymentCenterMediaRow> }
  | { tab: Exclude<DeploymentCenterTab, 'to-configure'>; counts: DeploymentCenterCounts; stacks: PaginatedResponse<DeploymentCenterStackRow> }

/**
 * Which tab a stack group belongs on.
 *
 * Only a **live** version can be out of sync. A pending version is rebuilt in place by every recompute, so
 * its snapshot is the current build by construction and there is no older build of it to be stale against.
 * Drift is therefore a question about what a media is *served*, and only a deployed version answers it.
 *
 * A group holding a pending version is ready to deploy even when a live version of it has drifted —
 * deploying is what resolves the drift, so that is the action to offer.
 *
 * That puts this the other way round from the media status (`statusOfCounters`),
 * which answers drift first. The divergence is deliberate: this page answers "what can I act on right
 * now", the media list answers "is this media correct". Do not align them.
 *
 * Callers must pass only `to-deploy`/`live` versions. An archived-only group would otherwise fall to
 * `up-to-date` and read as "nothing to do" when the truth is "nothing deployed".
 */
export const stackGroupStateOf = (versions: readonly Pick<DeploymentCenterStackVersion, 'status' | 'isOutOfSync'>[]): StackGroupState =>
  versions.some((v) => v.status === 'to-deploy') ? 'to-deploy' : versions.some((v) => v.status === 'live' && v.isOutOfSync) ? 'out-of-sync' : 'up-to-date'

/**
 * The live version a group's next version is built from, and that the deploy which follows takes over: its
 * newest.
 *
 * One function for both halves of the flow, because the two have to agree on the same version. "Create
 * updated version" copies this one's configuration; the deploy archives this same one and inherits its share
 * of the traffic. A group mid-experiment therefore keeps its other live version serving the rest, and the
 * split still adds up — which is what `liveGroupViolation` demands of the resulting state.
 *
 * `undefined` for a group with nothing live: every group before its first deployment.
 */
export const newestLiveVersionOf = <V extends Pick<DeploymentCenterStackVersion, 'status' | 'version'>>(versions: readonly V[]): V | undefined =>
  versions.filter((v) => v.status === 'live').reduce<V | undefined>((newest, v) => (newest === undefined || v.version > newest.version ? v : newest), undefined)

/**
 * Whether a group is mid-experiment: more than one version serving traffic. Drives the deploy's confirmation,
 * never a filter.
 *
 * There is deliberately no companion asking whether an updated version *may* be created. An A/B group used to
 * be refused one, and that refusal was on the wrong action: creating a version writes a new pending document
 * and touches no live one. The fear it was written for — silently changing a running experiment — belongs to
 * the deploy, which is where this is read.
 */
export const isAbDeployedGroup = (versions: readonly Pick<DeploymentCenterStackVersion, 'status'>[]): boolean => versions.filter((v) => v.status === 'live').length > 1

/**
 * Which stack types a viewer may see — the Node-visibility gate, in one place.
 *
 * A scope that never bought Node does not operate Node stacks, so its screens must not list any. One can
 * still hold a leftover: before the server-side variant, activating Spark on a One media created one,
 * and it stays hidden here until the cleanup script drops it.
 *
 * Applied to rows **and** to counts by living in the filter both read, so the UI cannot widen it and a
 * badge cannot count what its table hides. `requested` is the user's own type facet: it can only narrow
 * what the gate already allows, never reopen it.
 *
 * An empty result is a legitimate answer (a customer without Node explicitly asking for `pbs`), and
 * yields an empty list rather than a refusal: this is a filter, not an authorisation boundary, and a 403
 * would confirm that the hidden stacks exist.
 */
export const deploymentCenterStackTypes = ({ hasNodeAccess }: { hasNodeAccess: boolean }, requested?: DeploymentCenterStackType): DeploymentCenterStackType[] => {
  const allowed: DeploymentCenterStackType[] = hasNodeAccess ? ['pbs', 'adm'] : ['adm']
  return requested ? allowed.filter((type) => type === requested) : allowed
}

/**
 * How a queue is narrowed. One shape, read by the list **and** by the bulk action, so the set you act on
 * is the set you were shown by construction rather than by the two agreeing on a list of ids.
 */
export type DeploymentCenterQuery = {
  tab: DeploymentCenterTab
  search?: string
  type?: DeploymentCenterStackType
}

/**
 * What a bulk action acts on: **the queue**, not a list of stacks the client fetched first.
 *
 * This is what makes the page usable at a thousand stacks. Pagination caps a page at 100
 * (`withPagination`'s `MAX_LIMIT`), and a selection can only hold what was fetched, so an id-driven
 * "deploy everything" costs ten-plus round trips and a payload built client-side. Sending the query
 * instead costs one request whatever the size of the queue, and the server resolves the set with the
 * same aggregation — and the same Node gate — that produced the rows and the badge.
 *
 * `only` is the ten-stack customer: tick three rows and the action narrows to them. Omitted or empty
 * means the whole queue, which is the thousand-stack customer. Same endpoint, no modes.
 *
 * There is deliberately no `expectedCount`. The queue is live, so what the badge showed and what the
 * server resolves can differ — but the plan/apply split already handles that honestly: the plan is
 * fetched on the click and the confirmation states the number the server actually resolved, so the user
 * agrees to that figure rather than to the one the button happened to be showing.
 */
export type DeploymentCenterActionRequest = DeploymentCenterQuery & {
  only?: string[]
}

/**
 * How many stacks one write request carries.
 *
 * This is a **hard** ceiling, not a tuning knob: API Gateway kills a request at 29s. Measured on a large
 * production scope (~480 kB of stacks), creating updated versions costs ~9s per 100 stacks and deploying
 * ~2s per 100 — so a thousand-stack run is minutes of work and cannot be one request whatever the server
 * does internally. 150 keeps roughly a 2x margin on the slower of the two.
 *
 * So the client paces the run, one request per chunk, which is also what makes progress reportable.
 * Closing the tab mid-run leaves the queue partly done. That is acceptable because the queue is
 * self-describing: re-running picks up whatever is still pending.
 *
 * A chunk is not atomic. `applyDeploymentUpdates` is an ordered `bulkWrite` with no transaction. What
 * holds a deploy together is `chunkGroups`, which never splits a group, so a failure costs whole groups
 * rather than half of one.
 */
export const BULK_ACTION_CHUNK_SIZE = 150

export const chunk = <T>(items: readonly T[], size: number = BULK_ACTION_CHUNK_SIZE): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

/** Why a row the action resolved was not acted on. */
export const DEPLOYMENT_SKIP_REASONS = ['outdated', 'not-found', 'wrong-scope'] as const
export type DeploymentSkipReason = (typeof DEPLOYMENT_SKIP_REASONS)[number]

/**
 * What a bulk action reports back.
 *
 * A partial result is the normal case at this size, not an error: a queue of a thousand will routinely
 * hold a handful of A/B-deployed groups the action must refuse. Counting them separately is what lets the
 * page say "982 deployed, 18 skipped" instead of failing the whole call or claiming a clean run.
 */
export type DeploymentCenterActionResult = {
  /** How many rows the query resolved to, after `only` and the gate. */
  resolved: number
  succeeded: number
  skipped: { stackGroupId: string; reason: DeploymentSkipReason }[]
  /**
   * Set when the run stopped early, carrying the groups it never reached.
   *
   * Kept out of `skipped`, which counts groups refused on purpose with a reason a publisher can read.
   * Folding 500 never-reached groups in with 18 refusals as "518 skipped" is the misreport this avoids.
   */
  stopped?: { notAttempted: number }
}

/**
 * One request's worth of a deploy run.
 *
 * It carries the writes themselves rather than the ids of the stacks to promote, and that is the point.
 * `updatedAt` on a `BulkUpdateStack` is the optimistic-concurrency token: `bulkDeployStacks` refuses a
 * payload whose `updatedAt` does not match the stored one ("Outdated stack snapshot"), which is what
 * stops a page left open all afternoon from deploying a version somebody else has changed since. A chunk
 * of ids would throw that token away. Working the status, the ratio and the priority out again when the
 * chunk is applied would also mean re-running the queue aggregation once per chunk, to answer a question
 * the plan has already answered.
 */
export type DeployChunk = {
  /** Ready to send, in order, exactly as the deployment expects. Carries no snapshot or configuration. */
  updates: BulkUpdateStack[]
  /**
   * The group each promotion in `updates` deploys, by the promoted stack's id. The write reports a refusal
   * by stack id, while the rest of the plan speaks in groups, so this is what keys a skip back to its group.
   */
  groupByPromotionId: Record<string, string>
}

/** One request's worth of a "create updated version" run. Only ids travel — see {@link NewVersionPlan}. */
export type NewVersionChunk = {
  /** The latest drifted version of each eligible group — the stack each new version is copied from. */
  stackIds: string[]
}

/**
 * What the server answers when asked to deploy a queue: **the plan, not the outcome**.
 *
 * The split is forced by the 29s ceiling above — no single request can carry a thousand-stack run — but
 * it also puts each half where it belongs. Deciding *what* to write is the part that must be gated,
 * consistent with the list, and impossible to widen from the browser, so the server resolves it from the
 * query in one cheap call. Pacing the writes is the part that needs to survive a timeout and show
 * progress, so the client sends the chunks one at a time and counts them as they land.
 *
 * The cutting is the server's too. It already knows the ceiling and the measured rates the chunk size was
 * picked from, and a client that slices for itself is a client that can slice wrong — which shows up as a
 * gateway timeout on the largest scopes and nowhere else.
 *
 * The alternative — the client walking every page of the list to build the payload itself — costs one
 * request per hundred rows before a single stack is written, and re-derives on the client a set the
 * server already knows how to compute.
 */
export type DeploymentPlan = {
  /** The run, already cut into requests. Send them in order; the last one finishes the queue. */
  chunks: DeployChunk[]
  /** Stack groups the plan covers — what the confirmation counts, and not the same as the writes it holds. */
  resolved: number
  /**
   * How many of those groups are mid-experiment, so the confirmation can name a number rather than a
   * possibility.
   *
   * Counted here because the client cannot: a whole-queue run never holds its rows, and that is precisely
   * the run where the warning matters most. Deriving it from the selection would leave every large deploy
   * with a "may hold A/B stacks" notice, including the scopes that have none.
   */
  abDeployed: number
  /** Groups deliberately left out, so the UI can say why rather than silently narrowing. */
  skipped: { stackGroupId: string; reason: DeploymentSkipReason }[]
}

/**
 * The Out of sync counterpart to `DeploymentPlan`: which stacks to clone, not the clones themselves.
 *
 * Only ids travel. Creating an updated version means copying a stack's `configuration` into a new
 * version, and a One stack's configuration carries every ad unit and header-bidding setting it holds —
 * sending a thousand of those to the browser so it can send them straight back would be the largest
 * payload on the page, twice, to move data that never leaves the server's own database.
 *
 * So the payload lands differently from the deploy one: the client names the stacks and the server does
 * the copying. Chunks are still capped at `BULK_ACTION_CHUNK_SIZE` — creating updated versions is the
 * slower of the two measured operations (~9s per 100), so it is the one the 29s gateway limit binds
 * hardest.
 */
export type NewVersionPlan = {
  /** The run, already cut into requests, exactly as {@link DeploymentPlan} cuts a deploy. */
  chunks: NewVersionChunk[]
  /** Stack groups the plan covers, including the ones it had to skip. */
  resolved: number
  skipped: { stackGroupId: string; reason: DeploymentSkipReason }[]
}

/** Every stack a plan would copy, across its chunks: what the confirmation counts before anything is sent. */
export const newVersionPlanStackIds = (plan: Pick<NewVersionPlan, 'chunks'>): string[] => plan.chunks.flatMap(({ stackIds }) => stackIds)

/** Every write a plan holds, across its chunks. Promotions and the archives that accompany them, in order. */
export const deployPlanUpdates = (plan: Pick<DeploymentPlan, 'chunks'>): BulkUpdateStack[] => plan.chunks.flatMap(({ updates }) => updates)

/** How a run in progress reports itself. `collecting` is the plan request; `applying` walks the chunks. */
export type BulkProgress = { phase: 'collecting' | 'applying'; done: number; total: number }

/**
 * Whether the action covers the whole queue rather than a ticked subset.
 *
 * An empty `only` is deliberately the same as an absent one. A client that sends `only: []` has a
 * selection of nothing, and the honest reading of "deploy nothing" is not "deploy everything" — but the
 * page never sends it, because the button is disabled with an empty selection. Treating the two alike
 * keeps the wire format forgiving; what guards against acting on more than intended is the plan the
 * confirmation is built from, which is resolved server-side at the moment of the click.
 */
export const isWholeQueueAction = (request: Pick<DeploymentCenterActionRequest, 'only'>): boolean => !request.only?.length
